import React, { memo, useEffect, useState } from "react";
import { useLocation, Outlet } from "react-router-dom";
import { useGlobalSlice } from "app/slice";
import { PurchaseFlowNavigationGuard } from "../purchase-flow-navigation-guard";
import { Header } from "./Header";

export const exactPaths = [
  "/network-plan",
  "/single-network-selection",
  "/single-network-phone-type",
  "/single-network-number-type",
  "/multi-network-selection",
  "/multi-network-phone-type",
  "/multi-network-number-type",
  "/multi-network-number-information",
  "/digital-security-selection",
  "/checkout",
  "/activation/fixed-phone/activation-code",
  "/activation/fixed-phone/choose-service-plan",
  "/activation/fixed-phone/one-time-offer",
  "/activation/fixed-phone/security-plan-free-trial",
  "/activation/preloaded-sim/device-info",
  "/activation/preloaded-sim/choose-service-plan",
  "/activation/preloaded-sim/security-plan-upgrade",
  "/my-account/dashboard",
  "/my-account/payment-history",
  "/my-account/manage-profile",
  "/my-account/manage-cards",
  "/my-account/change-password",
  "/my-account/sim-service",
];

/** Paths where the header is relative (not fixed). All other pages keep fixed header. */
export const headerRelativePaths = [
  "/network-plan",
  "/checkout",
  "/activation/fixed-phone/activation-code",
  "/activation/fixed-phone/choose-service-plan",
  "/activation/fixed-phone/one-time-offer",
  "/activation/fixed-phone/security-plan-free-trial",
  "/activation/preloaded-sim/device-info",
  "/activation/preloaded-sim/choose-service-plan",
  "/activation/preloaded-sim/security-plan-upgrade",
];

export function isHeaderRelativeForPath(pathname: string): boolean {
  return headerRelativePaths.some(
    (path) => pathname === path || pathname.startsWith(path + "/"),
  );
}

export const AdminLayout = memo(() => {
  const location = useLocation();
  const [showAppDownload, setShowAppDownload] = useState(false);
  const [showPromoBanner, setShowPromoBanner] = useState(false);
  const [showMaintenanceBanner, setShowMaintenanceBanner] = useState(false);

  useEffect(() => {
    setShowMaintenanceBanner(true);
  }, []);

  // useEffect(() => {
  //   setShowPromoBanner(true);
  // }, []);

  const shouldApplyMarginTop = !exactPaths.includes(location.pathname);

  const isHeaderRelative = isHeaderRelativeForPath(location.pathname);
  const isAgentPage = location.pathname.includes("agent");

  const { useGetFreeProductVariantQuery } = useGlobalSlice();
  const { data: freeProductData, isSuccess } = useGetFreeProductVariantQuery(
    undefined,
    {
      skip: !shouldApplyMarginTop || isAgentPage,
    },
  );
  const promoProductId = freeProductData?.data?.product?._id;
  const hasPromoProduct = !!promoProductId;

  // The banner is visible only if the user hasn't dismissed it AND we have a product to show AND API succeeded
  const isBannerVisible = showPromoBanner && hasPromoProduct && isSuccess;

  useEffect(() => {
    const checkBannerVisibility = () => {
      // Check if user permanently dismissed the banner
      const permanentDismiss = localStorage.getItem(
        "appBannerPermanentDismiss",
      );
      if (permanentDismiss === "true") {
        return false;
      }

      // Check if user dismissed temporarily (within last 7 days)
      const lastDismissed = localStorage.getItem("appBannerDismissed");
      const now = Date.now();

      if (
        lastDismissed &&
        now - parseInt(lastDismissed) < 7 * 24 * 60 * 60 * 1000
      ) {
        return false;
      }

      // Check if it's the first visit today or haven't seen it today
      const lastSeenDate = localStorage.getItem("appBannerLastSeen");
      if (
        !lastSeenDate ||
        new Date(parseInt(lastSeenDate)).toDateString() !==
        new Date().toDateString()
      ) {
        return true;
      }

      return false;
    };

    // Small delay for better UX (let page load first)
    const timer = setTimeout(() => {
      setShowAppDownload(checkBannerVisibility());
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = (permanent = false) => {
    setShowAppDownload(false);

    if (permanent) {
      localStorage.setItem("appBannerPermanentDismiss", "true");
    } else {
      localStorage.setItem("appBannerDismissed", Date.now().toString());
    }

    // Always update last seen date
    localStorage.setItem("appBannerLastSeen", Date.now().toString());
  };

  const handleOpenApp = () => {
    // Track that user clicked the download button
    localStorage.setItem("appBannerLastSeen", Date.now().toString());
    window.open(
      "https://appeningxyz-my.sharepoint.com/:f:/g/personal/nikunj_s_appening_xyz/EqneHGUQG7dIqle7AYLmDzgB1LtXD0phpyf4ouGFkejiMg?e=OzftmK",
      "_blank",
      "noopener,noreferrer",
    );
  };

  const shouldShowIntercom =
    location.pathname.includes("network-plan") ||
    location.pathname.includes("selection") ||
    location.pathname.includes("phone-type") ||
    location.pathname.includes("number-type") ||
    location.pathname.includes("number-information");

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [location.pathname]);

  // Update last visit timestamp on component mount
  useEffect(() => {
    const now = Date.now();
    localStorage.setItem("lastVisit", now.toString());
  }, []);

  return (
    <React.Fragment>
      <PurchaseFlowNavigationGuard />
      <div
        className={
          isHeaderRelative
            ? "relative z-10 w-full bg-white"
            : "fixed top-0 left-0 z-10 w-full bg-white"
        }
      >
        <Header
          isHeaderRelative={isHeaderRelative}
          showAppDownload={showAppDownload}
          handleDismiss={handleDismiss}
          handleOpenApp={handleOpenApp}
          // showPromoBanner={isBannerVisible}
          // setShowPromoBanner={setShowPromoBanner}
          promoProductId={promoProductId}
          showMaintenanceBanner={showMaintenanceBanner}
          setShowMaintenanceBanner={setShowMaintenanceBanner}
        />
      </div>
      <div
        className={`flex overflow-hidden relative flex-col w-full ${
          isHeaderRelative ? "" : "min-h-dvh sm:min-h-screen"
        } ${isAgentPage ? "bg-[#F9FAFB]" : "bg-white"}`}
      >
        <div
          className={`w-full overflow-y-auto flex-1 transition-all duration-300 ease-in-out ${
            isHeaderRelative
              ? "mt-0"
              : isAgentPage
              ? "mt-14 sm:mt-[4.375rem] lg:mt-[5.3125rem]"
              : shouldApplyMarginTop
              ? `${showAppDownload ? "mt-24" : "mt-24"} ${
                  showMaintenanceBanner
                    ? "mt-32 sm:mt-[9.625rem] lg:mt-[11.375rem]"
                    : "mt-24 sm:mt-[7.5rem] lg:mt-[9.125rem]"
                }`
              : "mt-16 sm:mt-20 lg:mt-24"
          }`}
        >
          {/* isBannerVisible ? "mt-32 sm:mt-[9.625rem] lg:mt-[11.375rem]" */}
          {/* showMaintenanceBanner ? 'mt-[8.5rem] sm:mt-[10.25rem] lg:mt-[12.125rem]' */}
          <Outlet />
        </div>
        {/* mt-36 */}
        {/* {shouldShowIntercom && (
          <div className="fixed right-3 sm:right-7 bottom-10 sm:bottom-14 z-50 hidden sm:flex justify-center items-center bg-[#FE4D01] w-[3rem] h-[3rem] sm:w-[3.5rem] sm:h-[3.5rem] lg:w-[4rem] lg:h-[4rem] p-3 sm:p-4 rounded-full cursor-pointer">
            <img
              src="/assets/intercom.svg"
              className="flex-shrink-0 w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8"
              alt="Intercom"
            />
          </div>
        )} */}
      </div>
    </React.Fragment>
  );
});
